import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import { BREEDER_REFUND_POLICY } from 'dpl/breeder_account/utils/constants';
import DogPlaceholderSvg from 'dpl/common/components/DogPlaceholderSvg';
import Icon from 'dpl/common/components/Icon';
import TooltipWrapper from 'dpl/common/tooltip/components/TooltipWrapper';
import SmartImage from 'dpl/components/SmartImage';
import { PAYMENT_REQUEST_CATEGORIES } from 'dpl/shared/constants/breeder_app';
import { capitalize } from 'dpl/shared/utils';
import FloatingTooltipWrapper from 'dpl/common/tooltip/components/FloatingTooltipWrapper';

import { ensureCurrencyIsPresent } from './util';

export default function StripeChargeModalPuppyCardV2({
  className,
  dog,
  litter,
  amountInDollars,
  paymentCategory,
  breederProfile,
  dogDescription,
  onEditClick
}) {
  const photoSize = '48px';
  const totalWithDollar = ensureCurrencyIsPresent(amountInDollars);
  const breedName = dog?.breed?.name || dog?.breed_name;
  const isDeposit = paymentCategory === PAYMENT_REQUEST_CATEGORIES.DEPOSIT;
  const paymentText = isDeposit ? 'Deposit' : 'Payment';
  const titleText = `${paymentText} for ${dog?.name ?? 'dog'}`;

  let depositRefundPolicy = breederProfile.deposit_refund_policy;
  let depositDescription = breederProfile.deposit_description;

  if (litter?.deposit_refund_policy) {
    depositRefundPolicy = litter.deposit_refund_policy;
    depositDescription = litter.deposit_description;
  }

  function renderSubtitle() {
    if (isDeposit && depositRefundPolicy) {
      return (
        <DepositPolicyDescription
          refundPolicy={capitalize(depositRefundPolicy)}
          depositDescription={depositDescription}
        />
      );
    }

    if (paymentCategory === PAYMENT_REQUEST_CATEGORIES.DOG_PAYMENT) {
      if (dogDescription) {
        return <p>{dogDescription}</p>;
      }

      if (breedName) {
        return <p>{breedName} puppy</p>;
      }

      if (litter?.name) {
        return <p>From {litter.name}&apos;s litter</p>;
      }

      return <p>From {breederProfile.name}</p>;
    }

    return null;
  }

  return (
    <div className={classnames('StripeChargeModalPuppyCardV2', className)}>
      <div className="row no-gutters items-center">
        <div className="col col-sm-auto mb-1 mb0-sm">
          <div className="f0 mr3">
            {dog?.profile_photo_url ? (
              <SmartImage
                src={dog?.profile_photo_url}
                width={photoSize}
                height={photoSize}
                className="br-100 bg-white"
                loadingClass="bg-near-white"
                crop
                lazy
              />
            ) : (
              <DogPlaceholderSvg
                className="br-100 bg-white"
                width={photoSize}
                height={photoSize}
                icon
              />
            )}
          </div>
        </div>
        <div className="col-12 col-sm order-last order-sm-0">
          <div className="row no-gutters items-center">
            <div className="col-12 col-sm">
              <p className="font-serif font-18 md:font-20">{titleText}</p>
              <div className="font-14 secondary">{renderSubtitle()}</div>
            </div>
            {amountInDollars > 0 && (
              <div className="f0 col-12 col-sm-auto mt-2 mt-sm-0">
                <AmountPill
                  value={totalWithDollar}
                  category={paymentCategory}
                />
              </div>
            )}
          </div>
        </div>
        {onEditClick && (
          <div className="col-auto self-start self-center-ns">
            <button
              type="button"
              className="bg-white pv2 ph2 br-100 b--gray-300 ba ml2"
              onClick={onEditClick}
            >
              <Icon
                name="fetch-edit-alt"
                className="stone-700"
                width="20px"
                height="20px"
              />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

StripeChargeModalPuppyCardV2.propTypes = {
  className: PropTypes.string,
  breederProfile: PropTypes.shape({
    profile_photo_url: PropTypes.string,
    name: PropTypes.string.isRequired,
    pay_url: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    deposit_refund_policy: PropTypes.string,
    deposit_description: PropTypes.string
  }).isRequired,
  amountInDollars: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  dog: PropTypes.shape({
    name: PropTypes.string.isRequired,
    profile_photo_url: PropTypes.string,
    breed_name: PropTypes.string,
    breed: PropTypes.shape({
      name: PropTypes.string
    })
  }),
  litter: PropTypes.shape({
    name: PropTypes.string,
    deposit_refund_policy: PropTypes.string,
    deposit_description: PropTypes.string
  }),
  dogDescription: PropTypes.string,
  paymentCategory: PropTypes.oneOf(Object.values(PAYMENT_REQUEST_CATEGORIES)),
  onEditClick: PropTypes.func
};

StripeChargeModalPuppyCardV2.defaultProps = {
  className: null,
  dog: null,
  dogDescription: null,
  litter: null,
  amountInDollars: null,
  paymentCategory: PAYMENT_REQUEST_CATEGORIES.DOG_PAYMENT,
  onEditClick: null
};

function DepositPolicyDescription({ refundPolicy, depositDescription }) {
  return (
    <div className="flex align-items-center">
      <p className="mr1">
        {refundPolicy === BREEDER_REFUND_POLICY.OTHER
          ? 'Deposit policy'
          : refundPolicy}
      </p>
      {depositDescription && (
        <TooltipWrapper
          tooltipTextClassName="pt18 pb10 ph5"
          title={depositDescription}
          minWidth="200px"
        >
          <p className="underline">View details</p>
        </TooltipWrapper>
      )}
    </div>
  );
}

DepositPolicyDescription.propTypes = {
  depositDescription: PropTypes.string,
  refundPolicy: PropTypes.string.isRequired
};

DepositPolicyDescription.defaultProps = {
  depositDescription: null
};

function AmountPill({ value, category }) {
  const preTaxLabel =
    category === PAYMENT_REQUEST_CATEGORIES.DOG_PAYMENT
      ? 'Before taxes & fees, calculated below'
      : 'Before fees, calculated below';

  return (
    <div className="AmountPill inline-flex items-center gap-6 br-pill bg-stone-700 font-14 lh-copy white pv1 ph3">
      <span>{value}</span>
      <FloatingTooltipWrapper
        tooltipTextClassName="pt18 pb10 ph5"
        title={preTaxLabel}
        minWidth="200px"
      >
        <Icon className="nr1" name="fetch-info" height="20px" width="20px" />
      </FloatingTooltipWrapper>
    </div>
  );
}

AmountPill.propTypes = {
  value: PropTypes.string.isRequired,
  category: PropTypes.oneOf(Object.values(PAYMENT_REQUEST_CATEGORIES))
    .isRequired
};
